/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Children,
  isValidElement,
  ReactPortal,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  cloneElement,
} from 'react';
import { createPortal } from 'react-dom';

import { MapFilter } from '@/lib/widgets/complex/map-filter';

import { FacilityType } from '@/lib/entities/domain/model/facility';
import { IComplexPage } from '@/lib/entities/page';

import { SettingsButton } from '@/lib/shared/components/Buttons';
import { CloseSign } from '@/lib/shared/icons';

import { Wrapper } from '@googlemaps/react-wrapper';
import cn from 'clsx';

import styles from './ComplexMap.module.scss';
import { markers } from './markers';

interface IComplexMap {
  center: google.maps.LatLngLiteral;
  propertyName: string;
  facilities: IComplexPage['facilities'];
  mapClassName?: string;
  filtersClassName?: string;
  settingsBtnClassName?: string;
}
export const ComplexMap = ({
  center,
  propertyName,
  facilities,
  mapClassName,
  filtersClassName,
  settingsBtnClassName,
}: IComplexMap) => {
  const [filters, setFilters] = useState<string[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const data = useMemo(() => {
    return facilities.filter(
      (data) => filters.includes(data.type) || filters.length === 0
    );
  }, [facilities, filters]);

  const toggleFilterQuery = useCallback(
    (value: string) => {
      if (filters.includes(value)) {
        setFilters((f) => f.filter((val) => val !== value));
      } else {
        setFilters((f) => [...f, value]);
      }
    },
    [filters]
  );

  return (
    <Wrapper
      libraries={['marker']}
      version="beta"
      apiKey={process.env.NEXT_PUBLIC_MAP_API_KEY as string}
    >
      <div className={cn(styles.map, mapClassName)}>
        <Map center={center} zoom={15}>
          <Marker position={center} title={propertyName} onClick={() => {}} />
          {data.map((data) => {
            return (
              <Marker
                title={data.name}
                position={data.point}
                type={data.type}
                key={data.point.lat}
              />
            );
          })}
        </Map>
        <MapFilter
          filters={filters}
          toggleFilterQuery={toggleFilterQuery}
          className={cn(
            filtersClassName,
            styles.map__filters,
            isVisible && styles['map__filters--visible']
          )}
        />
        <SettingsButton
          className={cn(styles.map__open, settingsBtnClassName)}
          onClick={() => setIsVisible(!isVisible)}
          replacementIcon={isVisible ? <CloseSign /> : undefined}
        />
      </div>
    </Wrapper>
  );
};

function Map({
  center,
  zoom,
  children,
}: {
  center: google.maps.LatLngLiteral;
  zoom: number;
  children?: any;
}) {
  const ref = useRef<HTMLDivElement>(null);

  const [map, setMap] = useState<google.maps.Map>();

  useEffect(() => {
    setMap(
      new window.google.maps.Map(ref.current as HTMLElement, {
        center,
        zoom,
        mapId: '1fd191cb062c240a',
        disableDefaultUI: true,
      })
    );
    if (map) {
      map.addListener('click', function () {
        try {
          if (infoWindow) infoWindow.close();
        } catch (_e) {
          //do nothing
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <div ref={ref} id="map" className={styles.map__map}></div>
      {Children.map(children, (child) => {
        if (isValidElement(child)) {
          // set the map prop on the child component
          // @ts-expect-error ddd
          return cloneElement(child, { map });
        }
      })}
    </>
  );
}

interface IMarkerProps extends google.maps.marker.AdvancedMarkerElementOptions {
  onClick?: () => void;
  children?: any;
  type?: FacilityType;
  id?: string;
  toggle?: (arg: string) => void;
}
let infoWindow: google.maps.InfoWindow;
const Marker = ({
  map,
  position,
  children,
  onClick,
  title,
  type,
  id,
  toggle,
}: IMarkerProps) => {
  const rootRef = useRef<ReactPortal>();
  const markerRef = useRef<any>();

  useEffect(() => {
    let container: HTMLDivElement;
    if (!rootRef.current) {
      container = document.createElement('div');
      container.classList.add(styles.marker__container);
      if (!type) {
        container.innerHTML = markers.PIN;
      } else {
        container.innerHTML = markers[type];
      }
      rootRef.current = createPortal(children, container, id as string);

      // const infoWindow = new google.maps.InfoWindow();
      infoWindow = new google.maps.InfoWindow();
      markerRef.current = new (google.maps.marker as any).AdvancedMarkerView({
        position,
        title,
        content: container,
      });
      const clickHandler = function () {
        try {
          infoWindow.close();
          infoWindow.setContent(markerRef.current.title);
          infoWindow.open(markerRef.current.map, markerRef.current);
          if (toggle && type) toggle(type);
        } catch (_e) {
          //do nothing
        }
      };
      markerRef.current.addEventListener('gmp-click', clickHandler);
    }

    return () => {
      markerRef.current.map = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    markerRef.current.position = position;
    markerRef.current.map = map;
    let listener: any;
    if (onClick) {
      listener = markerRef.current.addEventListener('gmp-click', onClick);
    }
    return () => listener && listener.remove();
  }, [map, position, children, onClick]);
  return <></>;
};
